// Shared bits for the market demo routes.

import { defineRoutes, demoError, type DemoCtx, type DemoHandler, type DemoRoute, type DemoRouteDef } from '../core';
import { DemoHttpError } from '../data/errors';

/** Wrap handlers so a DemoHttpError thrown by the data layer becomes an axios-shaped rejection. */
function guarded(handler: DemoHandler): DemoHandler {
  return (ctx: DemoCtx) => {
    try {
      return handler(ctx);
    } catch (error) {
      if (error instanceof DemoHttpError) return demoError(error.status, error.detail);
      throw error;
    }
  };
}

export function routes(defs: DemoRouteDef[]): DemoRoute[] {
  return defineRoutes(defs.map(([method, pattern, handler]): DemoRouteDef => [method, pattern, guarded(handler)]));
}

/** The backend's GenericResponse envelope. */
export function ok<T>(result: T): { result: T; error: null; response: { message: string } } {
  return { result, error: null, response: { message: 'OK' } };
}

export function bodyOf<T>(ctx: DemoCtx): Partial<T> {
  const body = ctx.body;
  if (typeof body === 'string') {
    try {
      return JSON.parse(body) as Partial<T>;
    } catch {
      return {};
    }
  }
  return body && typeof body === 'object' ? (body as Partial<T>) : {};
}

export function boolParam(value: unknown, fallback: boolean): boolean {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  return String(value).toLowerCase() === 'true';
}
